import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useRef } from 'react';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Icons, Tooltip } from '@/js/libs/ui/src/V2';
import { getTestId } from './helpers';
import { LinkTooltip } from './LinkTooltip';
import { PlaceholdersDropdown } from './PlaceholdersDropdown';
import { ToolbarItem, ToolbarItems, ToolbarWrapper } from './styled';
export const Toolbar = ({ editor, disabled, placeholders, additionalToolbarItems, isPreview, setIsPreview, withPreviewToggle, rawEditorValue, setRawEditorValue, rawEditorRef, dataTestId, allowImages, }) => {
    const imageInputRef = useRef(null);
    const isSelectedTextEmpty = editor.state.selection.empty;
    const isBoldActive = editor.isActive('bold');
    const isItalicActive = editor.isActive('italic');
    const isUnderlineActive = editor.isActive('underline');
    const isStrikeActive = editor.isActive('strike');
    const isSubActive = editor.isActive('subscript');
    const isSuperActive = editor.isActive('superscript');
    const isOrderedListActive = editor.isActive('orderedList');
    const isBulletListActive = editor.isActive('bulletList');
    const isLinkActive = editor.isActive('link');
    const handleBoldClick = () => {
        editor.chain().focus().toggleBold().run();
    };
    const handleItalicClick = useCallbackRef(() => {
        editor.chain().focus().toggleItalic().run();
    });
    const handleUnderlineClick = useCallbackRef(() => {
        editor.chain().focus().toggleUnderline().run();
    });
    const handleStrikeClick = useCallbackRef(() => {
        editor.chain().focus().toggleStrike().run();
    });
    const handleSubClick = useCallbackRef(() => {
        editor.chain().focus().toggleSubscript().run();
    });
    const handleSuperClick = useCallbackRef(() => {
        editor.chain().focus().toggleSuperscript().run();
    });
    const handleOrderedListClick = useCallbackRef(() => {
        editor.chain().focus().toggleOrderedList().run();
    });
    const handleBulletListClick = useCallbackRef(() => {
        editor.chain().focus().toggleBulletList().run();
    });
    const handleLinkClick = useCallbackRef(() => {
        if (isSelectedTextEmpty) {
            return;
        }
        if (isLinkActive) {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
        }
    });
    const handleSaveLink = useCallbackRef((href) => {
        editor.chain().focus().extendMarkRange('link').toggleLink({ href }).run();
    });
    const handleClearClick = useCallbackRef(() => {
        editor.chain().focus().clearNodes().unsetAllMarks().run();
    });
    const handlePlaceholderClick = useCallbackRef((placeholder) => {
        if (isPreview) {
            editor.chain().focus().insertContent(`[${placeholder}]`).run();
        }
        else {
            if (!rawEditorRef?.current) {
                return;
            }
            const { selectionStart, selectionEnd } = rawEditorRef.current;
            setRawEditorValue((prev) => `${prev.substring(0, selectionStart)}[${placeholder}]${prev.substring(selectionEnd)}`);
        }
    });
    const handlePreviewClick = useCallbackRef(() => {
        setIsPreview((prev) => {
            if (prev) {
                setRawEditorValue(editor.getHTML());
            }
            else {
                editor.chain().focus().setContent(rawEditorValue).run();
            }
            return !prev;
        });
    });
    const handleImageClick = useCallbackRef(() => {
        imageInputRef.current?.click();
    });
    const handleImageChange = useCallbackRef((e) => {
        const { files } = e.target;
        if (files) {
            Array.from(files).forEach((file) => {
                const fileReader = new FileReader();
                fileReader.readAsDataURL(file);
                fileReader.onload = () => {
                    editor
                        .chain()
                        .insertContentAt(editor.state.selection.anchor, {
                        type: 'image',
                        attrs: {
                            src: fileReader.result,
                        },
                    })
                        .focus()
                        .run();
                };
            });
            if (imageInputRef.current) {
                imageInputRef.current.value = '';
            }
        }
    });
    return (_jsxs(ToolbarWrapper, { "data-testid": getTestId('toolbar', dataTestId), children: [_jsxs(ToolbarItems, { children: [isPreview && (_jsxs(_Fragment, { children: [_jsx(ToolbarItem, { type: 'button', "$active": isBoldActive, disabled: disabled, onClick: handleBoldClick, "data-testid": getTestId('toolbar-bold', dataTestId), children: _jsx(Icons.Bold, { size: 20 }) }), _jsx(ToolbarItem, { type: 'button', "$active": isItalicActive, disabled: disabled, onClick: handleItalicClick, "data-testid": getTestId('toolbar-italic', dataTestId), children: _jsx(Icons.Italic, { size: 20 }) }), _jsx(ToolbarItem, { type: 'button', "$active": isUnderlineActive, disabled: disabled, onClick: handleUnderlineClick, "data-testid": getTestId('toolbar-underline', dataTestId), children: _jsx(Icons.Underline, { size: 20 }) }), _jsx(ToolbarItem, { type: 'button', "$active": isStrikeActive, disabled: disabled, onClick: handleStrikeClick, "data-testid": getTestId('toolbar-strike', dataTestId), children: _jsx(Icons.Strike, { size: 20 }) }), _jsx(ToolbarItem, { type: 'button', "$active": isSubActive, disabled: disabled, onClick: handleSubClick, "data-testid": getTestId('toolbar-subscript', dataTestId), children: _jsx(Icons.Subscript, { size: 20 }) }), _jsx(ToolbarItem, { type: 'button', "$active": isSuperActive, disabled: disabled, onClick: handleSuperClick, "data-testid": getTestId('toolbar-superscript', dataTestId), children: _jsx(Icons.Superscript, { size: 20 }) }), _jsx(ToolbarItem, { type: 'button', "$active": isOrderedListActive, disabled: disabled, onClick: handleOrderedListClick, "data-testid": getTestId('toolbar-ordered-list', dataTestId), children: _jsx(Icons.OrderedList, { size: 20 }) }), _jsx(ToolbarItem, { type: 'button', "$active": isBulletListActive, disabled: disabled, onClick: handleBulletListClick, "data-testid": getTestId('toolbar-bullet-list', dataTestId), children: _jsx(Icons.BulletList, { size: 20 }) }), allowImages && (_jsxs(ToolbarItem, { type: 'button', disabled: disabled, onClick: handleImageClick, "data-testid": getTestId('toolbar-image', dataTestId), children: [_jsx(Icons.Image, { size: 20 }), _jsx("input", { type: 'file', accept: 'image/*', onChange: handleImageChange, hidden: true, ref: imageInputRef })] }))] })), placeholders && (_jsx(PlaceholdersDropdown, { placeholders: placeholders, onPlaceholderClick: handlePlaceholderClick, disabled: disabled, dataTestId: dataTestId })), isPreview && (_jsxs(_Fragment, { children: [_jsx(Tooltip, { type: 'popper', placement: 'bottom', trigger: 'click', arrow: false, disabled: isSelectedTextEmpty || isLinkActive, tooltip: _jsx(LinkTooltip, { editor: editor, saveLink: handleSaveLink, isCreating: true, dataTestId: dataTestId }), renderTooltipWithoutWrapper: true, children: _jsx(ToolbarItem, { type: 'button', "$active": isLinkActive, disabled: disabled, onClick: handleLinkClick, "data-testid": getTestId('toolbar-link', dataTestId), children: _jsx(Icons.Link, { size: 20 }) }) }), _jsx(ToolbarItem, { type: 'button', disabled: disabled, onClick: handleClearClick, "data-testid": getTestId('toolbar-clear', dataTestId), children: _jsx(Icons.Clear, { size: 20 }) })] })), additionalToolbarItems?.map(({ Icon, onClick }, index) => (_jsx(ToolbarItem, { type: 'button', disabled: disabled, onClick: onClick, "data-testid": getTestId(`toolbar-custom-${index}`, dataTestId), children: _jsx(Icon, { size: 20 }) }, index)))] }), _jsx(ToolbarItems, { children: withPreviewToggle && (_jsx(ToolbarItem, { type: 'button', onClick: handlePreviewClick, "data-testid": getTestId('toolbar-preview-toggle', dataTestId), children: _jsx(Icons.Preview, { size: 20, filled: !isPreview }) })) })] }));
};
